import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  Badge,
} from 'reactstrap';
import Loading from '../../components/Loading';
import { ventasService } from '../../services/ventasService';
import { cotizacionesService } from '../../services/cotizacionesService';
import { productosService } from '../../services/productosService';
import { clientesService } from '../../services/clientesService';
import { inventarioService } from '../../services/inventarioService';
import { empresasService } from '../../services/empresasService';

// Métodos de pago hardcodeados (deberían venir de un endpoint)
const METODOS_PAGO = [
  { MetodoPagoID: 1, Nombre: 'Efectivo' },
  { MetodoPagoID: 2, Nombre: 'Transferencia' },
  { MetodoPagoID: 3, Nombre: 'Tarjeta' },
  { MetodoPagoID: 4, Nombre: 'Otro' },
];

function VentaForm({ sucursalId, usuarioId, onSuccess, onCancel, cotizacionFolio = null, cotizacionId = null }) {
  const [formData, setFormData] = useState({
    ClienteID: null,
    Folio: '', // Se generará automáticamente si está vacío
    Observaciones: '',
  });
  const IVA_PORCENTAJE = 16; // IVA fijo del 16%
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [buscarCliente, setBuscarCliente] = useState('');
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [detalles, setDetalles] = useState([]);
  const [productoBuscar, setProductoBuscar] = useState('');
  const [productosEncontrados, setProductosEncontrados] = useState([]);
  const [buscandoProducto, setBuscandoProducto] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [metodoPagoSeleccionado, setMetodoPagoSeleccionado] = useState('');
  const [montoPago, setMontoPago] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [empresa, setEmpresa] = useState(null);
  const [buscarCotizacion, setBuscarCotizacion] = useState('');
  const [buscandoCotizacion, setBuscandoCotizacion] = useState(false);
  const [cotizacionCargada, setCotizacionCargada] = useState(false);

  // Función para resetear el formulario a su estado inicial
  const resetearFormulario = () => {
    setFormData({
      ClienteID: null,
      Folio: '',
      Observaciones: '',
    });
    setClienteSeleccionado(null);
    setBuscarCliente('');
    setClientesEncontrados([]);
    setBuscandoCliente(false);
    setDetalles([]);
    setProductoBuscar('');
    setProductosEncontrados([]);
    setBuscandoProducto(false);
    setMetodosPago([]);
    setMetodoPagoSeleccionado('');
    setMontoPago('');
    setError('');
    setBuscarCotizacion('');
    setBuscandoCotizacion(false);
    setCotizacionCargada(false);
  };

  useEffect(() => {
    loadEmpresa();
    // Resetear formulario al montar
    resetearFormulario();
    
    // Si hay un folio o ID de cotización en los props, cargarla automáticamente
    if (cotizacionFolio || cotizacionId) {
      cargarCotizacion(cotizacionFolio || cotizacionId, cotizacionFolio ? 'folio' : 'id');
    } else {
      // Si no hay parámetros de cotización, asegurarse de que el formulario esté limpio
      resetearFormulario();
    }
  }, [cotizacionFolio, cotizacionId]); // Dependencias para resetear cuando cambian los props


  const loadEmpresa = async () => {
    try {
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        setEmpresa(empresaActiva);
      }
    } catch (err) {
      // Error al cargar empresa
    }
  };

  const cargarCotizacion = async (valor, tipo = 'folio') => {
    if (!valor || !valor.trim()) return;
    
    try {
      setBuscandoCotizacion(true);
      setError('');
      
      let response;
      if (tipo === 'folio') {
        response = await cotizacionesService.getByFolio(valor.trim());
      } else {
        response = await cotizacionesService.getById(parseInt(valor));
      }

      if (response.success && response.data) {
        const cotizacion = response.data;
        
        // Calcular porcentajes desde los valores de la cotización
        const subtotalCotizacion = (cotizacion.Subtotal !== undefined ? parseFloat(cotizacion.Subtotal) : (cotizacion.subtotal !== undefined ? parseFloat(cotizacion.subtotal) : 0));
        const descuentoTotalCotizacion = cotizacion.DescuentoTotal !== undefined ? parseFloat(cotizacion.DescuentoTotal) : (cotizacion.descuentoTotal !== undefined ? parseFloat(cotizacion.descuentoTotal) : 0);
        const impuestosCotizacion = cotizacion.Impuestos !== undefined ? parseFloat(cotizacion.Impuestos) : (cotizacion.impuestos !== undefined ? parseFloat(cotizacion.impuestos) : 0);
        const subtotalConDescuento = subtotalCotizacion - descuentoTotalCotizacion;
        
        // Calcular porcentaje de impuestos (aproximado)
        let impuestosPorcentaje = 0;
        if (subtotalConDescuento > 0 && impuestosCotizacion > 0) {
          impuestosPorcentaje = (impuestosCotizacion / subtotalConDescuento) * 100;
        }
        
        // Cargar datos de la cotización
        // Usar el tipo y valor original del descuento si están disponibles
        const descuentoTotalTipo = cotizacion.DescuentoTotalTipo || cotizacion.descuentoTotalTipo || 'monto';
        const descuentoTotalValor = cotizacion.DescuentoTotalValor !== undefined && cotizacion.DescuentoTotalValor > 0
          ? cotizacion.DescuentoTotalValor
          : (cotizacion.descuentoTotalValor !== undefined && cotizacion.descuentoTotalValor > 0
            ? cotizacion.descuentoTotalValor
            : descuentoTotalCotizacion);
        
        setFormData(prev => ({
          ...prev,
          ClienteID: cotizacion.ClienteID || cotizacion.clienteID || null,
          Folio: '', // Se generará automáticamente
          Observaciones: cotizacion.Observaciones || cotizacion.observaciones || '',
        }));

        // Cargar cliente si existe
        if (cotizacion.ClienteID || cotizacion.clienteID) {
          setClienteSeleccionado({
            ClienteID: cotizacion.ClienteID || cotizacion.clienteID,
            NombreRazonSocial: cotizacion.ClienteNombre || cotizacion.clienteNombre,
          });
        }

        // Cargar detalles con stock actualizado
        // Usar el SucursalID de la cotización si está disponible, de lo contrario usar el prop
        const sucursalIdDeCotizacion = cotizacion.SucursalID || cotizacion.sucursalID;
        const sucursalIdParaStock = sucursalIdDeCotizacion 
          ? parseInt(sucursalIdDeCotizacion, 10) 
          : (sucursalId ? parseInt(sucursalId, 10) : null);
        
        if (!sucursalIdParaStock || isNaN(sucursalIdParaStock)) {
          setError('No se ha seleccionado una sucursal válida. Por favor, seleccione una sucursal antes de convertir la cotización.');
          setBuscandoCotizacion(false);
          return;
        }

        const detallesConStock = await Promise.all(
          (cotizacion.Detalles || cotizacion.detalles || []).map(async (det) => {
            try {
              const productoId = det.ProductoID || det.productoID;
              if (!productoId) {
                throw new Error('ProductoID no encontrado');
              }

              const productoIdNum = parseInt(productoId, 10);
              if (isNaN(productoIdNum)) {
                throw new Error('ProductoID no válido');
              }

              const stockResponse = await inventarioService.getStock(
                productoIdNum,
                sucursalIdParaStock
              );
              
              let stock = 0;
              let stockMinimo = 0;
              
              if (stockResponse && stockResponse.success && stockResponse.data) {
                stock = stockResponse.data.StockActual !== undefined 
                  ? stockResponse.data.StockActual 
                  : (stockResponse.data.stockActual !== undefined ? stockResponse.data.stockActual : 0);
                stockMinimo = stockResponse.data.StockMinimo !== undefined 
                  ? stockResponse.data.StockMinimo 
                  : (stockResponse.data.stockMinimo !== undefined ? stockResponse.data.stockMinimo : 0);
              }

              // Calcular porcentaje de impuestos del detalle
              const subtotalDetalle = (det.PrecioUnitario !== undefined ? parseFloat(det.PrecioUnitario) : (det.precioUnitario !== undefined ? parseFloat(det.precioUnitario) : 0)) * 
                                     (det.Cantidad !== undefined ? parseFloat(det.Cantidad) : (det.cantidad !== undefined ? parseFloat(det.cantidad) : 0)) -
                                     (det.Descuento !== undefined ? parseFloat(det.Descuento) : (det.descuento !== undefined ? parseFloat(det.descuento) : 0));
              const impuestosDetalle = det.Impuestos !== undefined ? parseFloat(det.Impuestos) : (det.impuestos !== undefined ? parseFloat(det.impuestos) : 0);
              let impuestosPorcentajeDetalle = 0;
              if (subtotalDetalle > 0 && impuestosDetalle > 0) {
                impuestosPorcentajeDetalle = (impuestosDetalle / subtotalDetalle) * 100;
              }
              
              // Usar el tipo y valor original del descuento si están disponibles
              const descuentoTipoDetalle = det.DescuentoTipo || det.descuentoTipo || 'monto';
              const descuentoValorDetalle = det.DescuentoValor !== undefined && det.DescuentoValor > 0
                ? det.DescuentoValor
                : (det.descuentoValor !== undefined && det.descuentoValor > 0
                  ? det.descuentoValor
                  : (det.Descuento !== undefined ? parseFloat(det.Descuento) : (det.descuento !== undefined ? parseFloat(det.descuento) : 0)));
              
              return {
                ProductoID: det.ProductoID || det.productoID,
                ProductoNombre: det.ProductoNombre || det.productoNombre || '',
                ProductoSKU: det.ProductoSKU || det.productoSKU || '',
                Cantidad: det.Cantidad !== undefined ? parseFloat(det.Cantidad) : (det.cantidad !== undefined ? parseFloat(det.cantidad) : 0),
                PrecioUnitario: det.PrecioUnitario !== undefined ? parseFloat(det.PrecioUnitario) : (det.precioUnitario !== undefined ? parseFloat(det.precioUnitario) : 0),
                Descuento: descuentoValorDetalle,
                DescuentoTipo: descuentoTipoDetalle,
                ImpuestosPorcentaje: impuestosPorcentajeDetalle > 0 ? impuestosPorcentajeDetalle : IVA_PORCENTAJE, // Usar IVA de cotización o 16% por defecto
                StockDisponible: stock,
                StockMinimo: stockMinimo,
              };
            } catch (err) {
              
              // Intentar obtener stock de forma alternativa o mostrar error
              const subtotalDetalle = (det.PrecioUnitario !== undefined ? parseFloat(det.PrecioUnitario) : (det.precioUnitario !== undefined ? parseFloat(det.precioUnitario) : 0)) * 
                                     (det.Cantidad !== undefined ? parseFloat(det.Cantidad) : (det.cantidad !== undefined ? parseFloat(det.cantidad) : 0)) -
                                     (det.Descuento !== undefined ? parseFloat(det.Descuento) : (det.descuento !== undefined ? parseFloat(det.descuento) : 0));
              const impuestosDetalle = det.Impuestos !== undefined ? parseFloat(det.Impuestos) : (det.impuestos !== undefined ? parseFloat(det.impuestos) : 0);
              let impuestosPorcentajeDetalle = 0;
              if (subtotalDetalle > 0 && impuestosDetalle > 0) {
                impuestosPorcentajeDetalle = (impuestosDetalle / subtotalDetalle) * 100;
              }
              
              // Mostrar error pero continuar con el detalle
              setError(`Advertencia: No se pudo obtener el stock para el producto ${det.ProductoNombre || det.productoNombre || 'N/A'}. Verifique que el producto tenga inventario en la sucursal seleccionada.`);
              
              // Usar el tipo y valor original del descuento si están disponibles
              const descuentoTipoDetalle = det.DescuentoTipo || det.descuentoTipo || 'monto';
              const descuentoValorDetalle = det.DescuentoValor !== undefined && det.DescuentoValor > 0
                ? det.DescuentoValor
                : (det.descuentoValor !== undefined && det.descuentoValor > 0
                  ? det.descuentoValor
                  : (det.Descuento !== undefined ? parseFloat(det.Descuento) : (det.descuento !== undefined ? parseFloat(det.descuento) : 0)));
              
              return {
                ProductoID: det.ProductoID || det.productoID,
                ProductoNombre: det.ProductoNombre || det.productoNombre || '',
                ProductoSKU: det.ProductoSKU || det.productoSKU || '',
                Cantidad: det.Cantidad !== undefined ? parseFloat(det.Cantidad) : (det.cantidad !== undefined ? parseFloat(det.cantidad) : 0),
                PrecioUnitario: det.PrecioUnitario !== undefined ? parseFloat(det.PrecioUnitario) : (det.precioUnitario !== undefined ? parseFloat(det.precioUnitario) : 0),
                Descuento: descuentoValorDetalle,
                DescuentoTipo: descuentoTipoDetalle,
                ImpuestosPorcentaje: impuestosPorcentajeDetalle,
                StockDisponible: 0, // Se mostrará como 0 pero el usuario verá el error
                StockMinimo: 0,
              };
            }
          })
        );

        setDetalles(detallesConStock);
        setCotizacionCargada(true);
        setBuscarCotizacion('');
      } else {
        setError(response.Message || response.message || 'Cotización no encontrada');
      }
    } catch (err) {
      setError(err.message || 'Error al cargar la cotización');
    } finally {
      setBuscandoCotizacion(false);
    }
  };

  const buscarClientes = async () => {
    if (!buscarCliente.trim() || !empresa) return;
    try {
      setBuscandoCliente(true);
      const response = await clientesService.buscar(buscarCliente.trim(), empresa.EmpresaID || empresa.empresaID);
      if (response.success && response.data) {
        const clientesData = Array.isArray(response.data) ? response.data : [];
        setClientesEncontrados(clientesData);
      }
    } catch (err) {
    } finally {
      setBuscandoCliente(false);
    }
  };

  const seleccionarCliente = (cliente) => {
    setClienteSeleccionado(cliente);
    setFormData(prev => ({ ...prev, ClienteID: cliente.ClienteID || cliente.clienteID }));
    setBuscarCliente('');
    setClientesEncontrados([]);
  };

  const buscarProductos = async () => {
    if (!productoBuscar.trim() || !empresa) return;
    try {
      setBuscandoProducto(true);
      const response = await productosService.getAll(
        empresa.EmpresaID || empresa.empresaID,
        sucursalId,
        null,
        productoBuscar.trim()
      );
      if (response.success && response.data) {
        const productosData = Array.isArray(response.data) ? response.data : [];
        setProductosEncontrados(productosData);
      }
    } catch (err) {
    } finally {
      setBuscandoProducto(false);
    }
  };

  const agregarProducto = async (producto) => {
    try {
      // Verificar stock
      const stockResponse = await inventarioService.getStock(
        producto.ProductoID || producto.productoID,
        sucursalId
      );
      const stock = stockResponse.success && stockResponse.data
        ? (stockResponse.data.StockActual !== undefined ? stockResponse.data.StockActual : (stockResponse.data.stockActual !== undefined ? stockResponse.data.stockActual : 0))
        : 0;

      if (stock <= 0) {
        setError(`El producto ${producto.Nombre || producto.nombre} no tiene stock disponible`);
        return;
      }

      const stockMinimo = stockResponse.success && stockResponse.data
        ? (stockResponse.data.StockMinimo !== undefined ? stockResponse.data.StockMinimo : (stockResponse.data.stockMinimo !== undefined ? stockResponse.data.stockMinimo : 0))
        : 0;

      const nuevoDetalle = {
        ProductoID: producto.ProductoID || producto.productoID,
        ProductoNombre: producto.Nombre || producto.nombre,
        ProductoSKU: producto.SKU || producto.sku,
        Cantidad: 1,
        PrecioUnitario: producto.PrecioVentaGlobal !== undefined ? parseFloat(producto.PrecioVentaGlobal) : (producto.precioVentaGlobal !== undefined ? parseFloat(producto.precioVentaGlobal) : 0),
        Descuento: 0,
        DescuentoTipo: 'monto', // 'monto' o 'porcentaje'
        ImpuestosPorcentaje: IVA_PORCENTAJE, // IVA fijo 16%
        StockDisponible: stock,
        StockMinimo: stockMinimo,
      };

      setDetalles(prev => [...prev, nuevoDetalle]);
      setProductoBuscar('');
      setProductosEncontrados([]);
      setError('');
    } catch (err) {
      setError(err.message || 'Error al agregar producto');
    }
  };

  const actualizarDetalle = (index, campo, valor) => {
    setDetalles(prev => {
      const nuevos = [...prev];
      if (campo === 'DescuentoTipo') {
        nuevos[index] = { ...nuevos[index], [campo]: valor };
      } else if (campo === 'ImpuestosPorcentaje') {
        // No permitir editar el IVA, siempre es 16%
        nuevos[index] = { ...nuevos[index], ImpuestosPorcentaje: IVA_PORCENTAJE };
      } else {
        nuevos[index] = { ...nuevos[index], [campo]: parseFloat(valor) || 0 };
      }
      return nuevos;
    });
  };

  const eliminarDetalle = (index) => {
    setDetalles(prev => prev.filter((_, i) => i !== index));
  };

  const agregarMetodoPago = () => {
    if (!metodoPagoSeleccionado || !montoPago || parseFloat(montoPago) <= 0) {
      setError('Seleccione un método de pago y un monto válido');
      return;
    }

    const metodo = METODOS_PAGO.find(m => m.MetodoPagoID.toString() === metodoPagoSeleccionado);
    if (!metodo) {
      setError('Método de pago no válido');
      return;
    }

    setMetodosPago(prev => [...prev, {
      MetodoPagoID: metodo.MetodoPagoID,
      MetodoPagoNombre: metodo.Nombre,
      Monto: parseFloat(montoPago),
    }]);

    setMetodoPagoSeleccionado('');
    setMontoPago('');
    setError('');
  };

  const eliminarMetodoPago = (index) => {
    setMetodosPago(prev => prev.filter((_, i) => i !== index));
  };

  const calcularSubtotal = () => {
    // NUEVA LÓGICA: Descuento se aplica al precio unitario, luego se calcula subtotal
    return detalles.reduce((sum, det) => {
      // 1. Aplicar descuento al precio unitario
      let precioUnitarioConDescuento = det.PrecioUnitario;
      if (det.DescuentoTipo === 'porcentaje' && det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario * (1 - det.Descuento / 100);
      } else if (det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario - det.Descuento;
      }
      // 2. Calcular subtotal con precio unitario con descuento
      return sum + (precioUnitarioConDescuento * det.Cantidad);
    }, 0);
  };

  const calcularTotal = () => {
    const subtotal = calcularSubtotal();
    
    // Calcular suma de impuestos por detalle (IVA 16% fijo)
    const sumaImpuestosPorDetalle = detalles.reduce((sum, det) => {
      // 1. Aplicar descuento al precio unitario
      let precioUnitarioConDescuento = det.PrecioUnitario;
      if (det.DescuentoTipo === 'porcentaje' && det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario * (1 - det.Descuento / 100);
      } else if (det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario - det.Descuento;
      }
      // 2. Calcular subtotal con precio unitario con descuento
      const subtotalDetalle = precioUnitarioConDescuento * det.Cantidad;
      // 3. Calcular IVA 16% sobre el subtotal
      const porcentajeIva = det.ImpuestosPorcentaje > 0 ? det.ImpuestosPorcentaje : IVA_PORCENTAJE;
      const impuestosDetalle = subtotalDetalle * (porcentajeIva / 100);
      return sum + impuestosDetalle;
    }, 0);
    
    // Total = subtotal + impuestos por detalle (sin descuentos ni impuestos globales)
    return subtotal + sumaImpuestosPorDetalle;
  };

  const totalMetodosPago = () => {
    return metodosPago.reduce((sum, mp) => sum + mp.Monto, 0);
  };

  const validate = () => {
    if (detalles.length === 0) {
      setError('Debe agregar al menos un producto');
      return false;
    }

    if (metodosPago.length === 0) {
      setError('Debe agregar al menos un método de pago');
      return false;
    }

    const total = calcularTotal();
    const totalPagos = totalMetodosPago();
    const diferencia = totalPagos - total;

    // Solo validar si falta dinero (diferencia negativa)
    if (diferencia < -0.01) {
      const falta = Math.abs(diferencia);
      setError(`Falta dinero. El total de métodos de pago (${formatCurrency(totalPagos)}) es menor al total de la venta (${formatCurrency(total)}). Faltan ${formatCurrency(falta)}`);
      return false;
    }
    // Si sobra dinero (diferencia positiva), está bien, se genera vuelto

    // Validar stock
    for (const detalle of detalles) {
      if (detalle.Cantidad > detalle.StockDisponible) {
        setError(`La cantidad del producto ${detalle.ProductoNombre} excede el stock disponible (${detalle.StockDisponible})`);
        return false;
      }
      
      // Validar stock mínimo (permitir que quede exactamente en el mínimo)
      const stockMinimo = detalle.StockMinimo !== undefined ? detalle.StockMinimo : 0;
      const stockResultante = detalle.StockDisponible - detalle.Cantidad;
      if (stockResultante < stockMinimo) {
        setError(`No se puede realizar la venta. El stock resultante (${stockResultante.toFixed(2)}) sería menor al stock mínimo (${stockMinimo}) para el producto ${detalle.ProductoNombre}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const ventaData = {
        SucursalID: sucursalId,
        ClienteID: formData.ClienteID,
        UsuarioID: usuarioId,
        Folio: formData.Folio || null, // Si está vacío, el backend generará uno automáticamente
        DescuentoTotal: 0, // Ya no hay descuentos globales
        DescuentoTotalTipo: 'monto',
        ImpuestosPorcentaje: 0, // Ya no hay impuestos globales
        Observaciones: formData.Observaciones || null,
        Detalles: detalles.map(det => ({
          ProductoID: det.ProductoID,
          Cantidad: det.Cantidad,
          PrecioUnitario: det.PrecioUnitario,
          Descuento: det.Descuento || 0,
          DescuentoTipo: det.DescuentoTipo || 'monto',
          ImpuestosPorcentaje: det.ImpuestosPorcentaje || IVA_PORCENTAJE, // IVA 16% por defecto
        })),
        MetodosPago: metodosPago.map(mp => ({
          MetodoPagoID: mp.MetodoPagoID,
          Monto: mp.Monto,
        })),
      };

      const response = await ventasService.create(ventaData);

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al crear la venta');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Nueva Venta</CardTitle>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md="12">
                    <Alert color="info">
                      <strong>Cargar desde Cotización:</strong> Busca una cotización por folio para cargar sus datos automáticamente.
                    </Alert>
                    <InputGroup className="mb-3">
                      <Input
                        type="text"
                        placeholder="Buscar cotización por folio"
                        value={buscarCotizacion}
                        onChange={(e) => setBuscarCotizacion(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            cargarCotizacion(buscarCotizacion, 'folio');
                          }
                        }}
                      />
                      <InputGroupText>
                        <Button
                          color="info"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            cargarCotizacion(buscarCotizacion, 'folio');
                          }}
                          disabled={buscandoCotizacion || !buscarCotizacion.trim()}
                        >
                          {buscandoCotizacion ? 'Buscando...' : 'Cargar'}
                        </Button>
                      </InputGroupText>
                    </InputGroup>
                    {cotizacionCargada && (
                      <Badge color="success" className="mb-2">
                        ✓ Cotización cargada exitosamente
                      </Badge>
                    )}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md="6">
                    <label>Cliente (Opcional)</label>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Buscar cliente por nombre, RFC o correo"
                        value={buscarCliente}
                        onChange={(e) => setBuscarCliente(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            buscarClientes();
                          }
                        }}
                      />
                      <InputGroupText>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            buscarClientes();
                          }}
                          disabled={buscandoCliente}
                        >
                          <i className="tim-icons icon-zoom-split" />
                        </Button>
                      </InputGroupText>
                    </InputGroup>
                    {clientesEncontrados.length > 0 && (
                      <div className="list-group mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {clientesEncontrados.map((cliente) => (
                          <button
                            key={cliente.ClienteID || cliente.clienteID}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => seleccionarCliente(cliente)}
                          >
                            {cliente.NombreRazonSocial || cliente.nombreRazonSocial}
                            {cliente.RFC && ` - ${cliente.RFC || cliente.rfc}`}
                          </button>
                        ))}
                      </div>
                    )}
                    {clienteSeleccionado && (
                      <Badge color="success" className="mt-2">
                        Cliente: {clienteSeleccionado.NombreRazonSocial || clienteSeleccionado.nombreRazonSocial}
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 ml-2"
                          onClick={() => {
                            setClienteSeleccionado(null);
                            setFormData(prev => ({ ...prev, ClienteID: null }));
                          }}
                        >
                          <i className="tim-icons icon-simple-remove" />
                        </Button>
                      </Badge>
                    )}
                  </Col>
                  <Col md="3">
                    <label>Folio</label>
                    <Input
                      type="text"
                      value={formData.Folio}
                      onChange={(e) => setFormData(prev => ({ ...prev, Folio: e.target.value }))}
                      placeholder="Opcional"
                    />
                  </Col>
                  <Col md="3">
                    <label>Observaciones</label>
                    <Input
                      type="textarea"
                      value={formData.Observaciones}
                      onChange={(e) => setFormData(prev => ({ ...prev, Observaciones: e.target.value }))}
                      placeholder="Opcional"
                      rows="1"
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md="12">
                    <label>Agregar Producto</label>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Buscar producto por SKU, nombre o código de barras"
                        value={productoBuscar}
                        onChange={(e) => setProductoBuscar(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            buscarProductos();
                          }
                        }}
                      />
                      <InputGroupText>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            buscarProductos();
                          }}
                          disabled={buscandoProducto}
                        >
                          <i className="tim-icons icon-zoom-split" />
                        </Button>
                      </InputGroupText>
                    </InputGroup>
                    {productosEncontrados.length > 0 && (
                      <div className="list-group mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {productosEncontrados.map((producto) => (
                          <button
                            key={producto.ProductoID || producto.productoID}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => agregarProducto(producto)}
                          >
                            {producto.SKU || producto.sku} - {producto.Nombre || producto.nombre}
                            {' '}
                            <Badge color="info">
                              {formatCurrency(producto.PrecioVentaGlobal !== undefined ? producto.PrecioVentaGlobal : (producto.precioVentaGlobal !== undefined ? producto.precioVentaGlobal : 0))}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </Col>
                </Row>

                {detalles.length > 0 && (
                  <Row className="mb-3">
                    <Col md="12">
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Producto</th>
                            <th>SKU</th>
                            <th>Cantidad</th>
                            <th>Precio Unit.</th>
                            <th>Descuento</th>
                            <th>Impuestos %</th>
                            <th>Total</th>
                            <th>Stock</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalles.map((detalle, index) => {
                            // Calcular subtotal del detalle con nueva lógica (descuento al precio unitario)
                            // 1. Aplicar descuento al precio unitario
                            let precioUnitarioConDescuento = detalle.PrecioUnitario;
                            if (detalle.DescuentoTipo === 'porcentaje' && detalle.Descuento > 0) {
                              precioUnitarioConDescuento = detalle.PrecioUnitario * (1 - detalle.Descuento / 100);
                            } else if (detalle.Descuento > 0) {
                              precioUnitarioConDescuento = detalle.PrecioUnitario - detalle.Descuento;
                            }
                            // 2. Calcular subtotal con precio unitario con descuento
                            const subtotalDetalle = precioUnitarioConDescuento * detalle.Cantidad;
                            // 3. Calcular IVA 16% sobre el subtotal
                            const porcentajeIva = detalle.ImpuestosPorcentaje > 0 ? detalle.ImpuestosPorcentaje : IVA_PORCENTAJE;
                            const impuestosDetalle = subtotalDetalle * (porcentajeIva / 100);
                            const totalDetalle = subtotalDetalle + impuestosDetalle;
                            
                            const stockMinimo = detalle.StockMinimo !== undefined ? detalle.StockMinimo : 0;
                            const stockResultante = detalle.StockDisponible - detalle.Cantidad;
                            const violaStockMinimo = stockResultante < stockMinimo; // Permitir que quede exactamente en el mínimo
                            return (
                              <tr key={index}>
                                <td>{detalle.ProductoNombre}</td>
                                <td>{detalle.ProductoSKU}</td>
                                <td>
                                  <Input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={detalle.Cantidad || ''}
                                    onChange={(e) => actualizarDetalle(index, 'Cantidad', e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    style={{ width: '80px' }}
                                    invalid={violaStockMinimo}
                                  />
                                  {violaStockMinimo && (
                                    <div className="text-danger small mt-1">
                                      ⚠ El stock resultante ({stockResultante.toFixed(2)}) sería menor al mínimo ({stockMinimo})
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={detalle.PrecioUnitario || ''}
                                    onChange={(e) => actualizarDetalle(index, 'PrecioUnitario', e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    style={{ width: '100px' }}
                                  />
                                </td>
                                <td>
                                  <InputGroup>
                                    <Input
                                      type="select"
                                      value={detalle.DescuentoTipo || 'monto'}
                                      onChange={(e) => {
                                        setDetalles(prev => {
                                          const nuevos = [...prev];
                                          nuevos[index] = { ...nuevos[index], DescuentoTipo: e.target.value };
                                          return nuevos;
                                        });
                                      }}
                                      style={{ width: '90px' }}
                                    >
                                      <option value="monto">Monto</option>
                                      <option value="porcentaje">%</option>
                                    </Input>
                                    <Input
                                      type="number"
                                      min="0"
                                      step={detalle.DescuentoTipo === 'porcentaje' ? '0.1' : '0.01'}
                                      max={detalle.DescuentoTipo === 'porcentaje' ? '100' : undefined}
                                      value={detalle.Descuento || ''}
                                      onChange={(e) => actualizarDetalle(index, 'Descuento', e.target.value)}
                                      onFocus={(e) => e.target.select()}
                                      style={{ width: '100px' }}
                                      placeholder={detalle.DescuentoTipo === 'porcentaje' ? '%' : '$'}
                                    />
                                  </InputGroup>
                                </td>
                                <td>
                                  <div>
                                    <strong>{IVA_PORCENTAJE}%</strong>
                                    <small className="text-muted d-block">
                                      {formatCurrency(impuestosDetalle)}
                                    </small>
                                  </div>
                                </td>
                                <td>{formatCurrency(totalDetalle)}</td>
                                <td>
                                  <Badge color={detalle.Cantidad > detalle.StockDisponible ? 'danger' : 'success'}>
                                    {detalle.StockDisponible}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => eliminarDetalle(index)}
                                  >
                                    <i className="tim-icons icon-simple-remove" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                )}

                <Row className="mb-3">
                  <Col md="6">
                    <label>IVA</label>
                    <div>
                      <strong>{IVA_PORCENTAJE}%</strong>
                      <small className="text-muted d-block">Impuesto fijo aplicado a cada producto</small>
                    </div>
                  </Col>
                  <Col md="6">
                    {(() => {
                      const subtotal = calcularSubtotal();
                      
                      // Calcular suma de impuestos por detalle (IVA 16% fijo)
                      const sumaImpuestosPorDetalle = detalles.reduce((sum, det) => {
                        // 1. Aplicar descuento al precio unitario
                        let precioUnitarioConDescuento = det.PrecioUnitario;
                        if (det.DescuentoTipo === 'porcentaje' && det.Descuento > 0) {
                          precioUnitarioConDescuento = det.PrecioUnitario * (1 - det.Descuento / 100);
                        } else if (det.Descuento > 0) {
                          precioUnitarioConDescuento = det.PrecioUnitario - det.Descuento;
                        }
                        // 2. Calcular subtotal con precio unitario con descuento
                        const subtotalDetalle = precioUnitarioConDescuento * det.Cantidad;
                        // 3. Calcular IVA 16% sobre el subtotal
                        const porcentajeIva = det.ImpuestosPorcentaje > 0 ? det.ImpuestosPorcentaje : IVA_PORCENTAJE;
                        const impuestosDetalle = subtotalDetalle * (porcentajeIva / 100);
                        return sum + impuestosDetalle;
                      }, 0);
                      
                      const total = calcularTotal();
                      
                      return (
                        <>
                          <h5 className="mt-4">
                            Subtotal: {formatCurrency(subtotal)}
                          </h5>
                          {sumaImpuestosPorDetalle > 0 && (
                            <h6 className="text-muted">
                              IVA ({IVA_PORCENTAJE}%): +{formatCurrency(sumaImpuestosPorDetalle)}
                            </h6>
                          )}
                          <h5>
                            Total: {formatCurrency(total)}
                          </h5>
                        </>
                      );
                    })()}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md="12">
                    <label>Métodos de Pago</label>
                    <InputGroup>
                      <Input
                        type="select"
                        value={metodoPagoSeleccionado}
                        onChange={(e) => setMetodoPagoSeleccionado(e.target.value)}
                        style={{ width: '200px' }}
                      >
                        <option value="">Seleccione método</option>
                        {METODOS_PAGO.map((metodo) => (
                          <option key={metodo.MetodoPagoID} value={metodo.MetodoPagoID}>
                            {metodo.Nombre}
                          </option>
                        ))}
                      </Input>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="Monto"
                        value={montoPago}
                        onChange={(e) => setMontoPago(e.target.value)}
                        style={{ width: '150px' }}
                      />
                      <InputGroupText>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            agregarMetodoPago();
                          }}
                        >
                          <i className="tim-icons icon-simple-add" />
                        </Button>
                      </InputGroupText>
                    </InputGroup>
                    {metodosPago.length > 0 && (
                      <div className="mt-2">
                        {metodosPago.map((mp, index) => (
                          <Badge key={index} color="info" className="mr-2 mb-2">
                            {mp.MetodoPagoNombre}: {formatCurrency(mp.Monto)}
                            <Button
                              color="link"
                              size="sm"
                              className="p-0 ml-2"
                              onClick={() => eliminarMetodoPago(index)}
                            >
                              <i className="tim-icons icon-simple-remove" />
                            </Button>
                          </Badge>
                        ))}
                        <div className="mt-2">
                          <strong>Total Pagos: {formatCurrency(totalMetodosPago())}</strong>
                          {' '}
                          {(() => {
                            const total = calcularTotal();
                            const totalPagos = totalMetodosPago();
                            const diferencia = totalPagos - total;
                            
                            if (Math.abs(diferencia) < 0.01) {
                              return <Badge color="success">Completo</Badge>;
                            } else if (diferencia > 0.01) {
                              // Sobra dinero (vuelto)
                              return <Badge color="info">Vuelto: {formatCurrency(diferencia)}</Badge>;
                            } else {
                              // Falta dinero
                              return <Badge color="danger">Faltan {formatCurrency(Math.abs(diferencia))}</Badge>;
                            }
                          })()}
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col md="12" className="text-right">
                    <Button color="secondary" onClick={onCancel} className="mr-2">
                      Cancelar
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                      {loading ? 'Guardando...' : 'Guardar Venta'}
                    </Button>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default VentaForm;

